import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; // Assuming you are using react-router-dom
import './Activities.css'; // Import your CSS file for styles
import baseUrl from '../../config';
import CertificateCriteria from '../../Components/CertificateCriteria';


const ActivityCard = ({
  icon,
  title,
  description,
  buttonText,
  buttonLink,
  borderColor,
  isLocked = false,
  currentClasses = 0,
  requiredClasses = 0,
  detailsText,
  onButtonClick, // New prop for handling button clicks
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const toggleDetails = () => {
    setIsDetailsOpen(!isDetailsOpen);
  };

  // Calculate progress bar width for locked cards
  const progressBarWidth = isLocked
    ? `${(currentClasses / requiredClasses) * 100}%`
    : '0%';

  return (
    <div
      className={`
        activity-card p-4 flex flex-col items-center text-center rounded-2xl shadow-lg transition-all duration-300 ease-in-out
        ${isLocked ? 'locked relative' : ''}
      `}
      // Apply border color dynamically based on locked state or prop
      style={{ borderColor: isLocked ? 'var(--gray-400)' : borderColor }}
      data-current-classes={currentClasses}
      data-required-classes={requiredClasses}
    >
      {/* Icon and Title */}
      <span className="text-5xl mb-3">{icon}</span> {/* Reduced icon size */}
      <h3 className="font-bold text-lg text-gray-800 mb-1">{title}</h3> {/* Reduced title size */}
      <p className="text-sm text-gray-600 mb-3 flex-grow">{description}</p> {/* Reduced description size */}

      {/* Action Button */}
      <button
        className={`
          px-4 py-2 rounded-full font-semibold text-sm w-full transition-colors duration-200
          ${isLocked
            ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
            : 'bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50'
          }
        `}
        disabled={isLocked}
        onClick={onButtonClick} // Use the onButtonClick prop
      >
        {buttonText}
      </button>

      {/* Details Toggle Button */}
      {detailsText && (
        <>
          <button
            className={`
              toggle-details-btn mt-3 px-3 py-1.5 rounded-full flex items-center justify-center space-x-1 text-xs text-gray-700 bg-gray-100
              hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors duration-200
              ${isDetailsOpen ? 'open' : ''}
            `}
            onClick={toggleDetails}
            aria-expanded={isDetailsOpen}
          >
            <span>Details</span>
            <svg
              className="w-3 h-3 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          {/* Activity Details Section */}
          <div className={`activity-details text-left text-gray-700 w-full ${isDetailsOpen ? 'open' : ''}`}>
            <p className="text-xs mt-1.5">{detailsText}</p> {/* Reduced details text size */}
            {isLocked && (
              <p className="text-xs mt-1">
                <span className="font-semibold">Required Classes:</span> {requiredClasses}
              </p>
            )}
            {!isLocked && buttonText === 'Go to Writing Space' && (
                <p className="text-xs mt-1 font-semibold">Current Progress: Active</p>
            )}
          </div>
        </>
      )}

      {/* Locked Overlay */}
      {isLocked && (
        <div className="locked-overlay absolute inset-0 bg-black bg-opacity-50 rounded-2xl flex flex-col items-center justify-center text-white p-3 backdrop-blur-sm z-10">
          <svg
            className="w-10 h-10 text-white mb-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v3h8z"></path>
          </svg>
          <p className="text-lg font-bold mb-1.5">Locked</p> {/* Reduced text size */}
          <div className="progress-bar-container w-4/5 bg-gray-200 rounded-full h-1.5 overflow-hidden"> {/* Reduced height */}
            <div
              className="progress-bar-fill h-full rounded-full bg-orange-500 transition-all duration-500 ease-out"
              style={{ width: progressBarWidth }}
            ></div>
          </div>
          <p className="text-xs mt-1">Progress: {currentClasses}/{requiredClasses} classes</p> {/* Reduced text size */}
        </div>
      )}
    </div>
  );
};

const ActivitiesSection = ({ title, iconSvgPath, iconColorClass, borderColorClass, activities }) => {
  return (
    <div className={`bg-white rounded-2xl shadow-lg p-6 border-b-4 ${borderColorClass} mb-6 transform-gpu transition-all duration-300 hover:scale-[1.005]`}>
      <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-6 flex items-center">
        <svg className={`w-8 h-8 md:w-10 md:h-10 mr-3 ${iconColorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={iconSvgPath}></path>
        </svg>
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activities.map((activity, index) => (
          <ActivityCard
            key={activity.id || index} // Use unique ID if available, otherwise index
            icon={activity.icon}
            title={activity.title}
            description={activity.description}
            buttonText={activity.buttonText}
            buttonLink={activity.buttonLink}
            borderColor={activity.borderColor}
            isLocked={activity.isLocked}
            currentClasses={activity.currentClasses}
            requiredClasses={activity.requiredClasses}
            detailsText={activity.detailsText}
            onButtonClick={activity.onButtonClick} // Pass the click handler
          />
        ))}
      </div>
    </div>
  );
};

const Activities = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleClickClose = () => setDrawerOpen(false);
  const handleClickOpen = () => setDrawerOpen(true);

  const navigate = useNavigate();

  const handleCreateNotes = () => {
    navigate('/createNotes'); // Navigate to the CreateNotes page
  };

  // Fetch enrichment classes from the API
  useEffect(() => {
    const fetchEnrichmentClasses = async () => {
      const token = localStorage.getItem('token');

      try {
        const response = await axios.get(`${baseUrl}api/students/enrichment-classes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        console.log("API Response:", response.data);

        // Sort classes to show unlocked classes first
        const sortedClasses = response.data.sort((a, b) => (a.locked === b.locked ? 0 : a.locked ? 1 : -1));
        setClasses(sortedClasses);
      } catch (err) {
        console.error('Error fetching enrichment classes:', err);
        setError('Failed to load enrichment classes.');
      } finally {
        setLoading(false);
      }
    };

    fetchEnrichmentClasses();
  }, []);

  // Filter and map classes data to ActivityCard props
  const filteredClasses = classes.filter(classItem => {
    const matchesSearch = classItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          classItem.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterType === 'all' ||
      (filterType === 'unlocked' && !classItem.locked) ||
      (filterType === 'locked' && classItem.locked);

    return matchesSearch && matchesFilter;
  });

  const enrolledActivities = filteredClasses
    .filter(classItem => !classItem.locked && classItem.writing) // Assuming 'writing' indicates an enrolled activity for "Writing Space"
    .map(classItem => ({
      id: classItem._id, // Assuming _id is your unique identifier
      icon: '✍️', // You might want to map icons based on class type
      title: classItem.title || 'Writing Space', // Use classItem.title if available, otherwise default
      description: classItem.description || 'A supportive environment for writers to grow and share.',
      buttonText: 'Go to Writing Space',
      buttonLink: '#',
      borderColor: '#34d399', // green-500
      detailsText: classItem.details || 'This is where you can practice creative writing, get feedback, and collaborate with other young writers. Perfect for aspiring authors!',
      onButtonClick: handleCreateNotes, // Link to the CreateNotes page
    }));

  const exploreActivities = filteredClasses
    .filter(classItem => classItem.locked || !classItem.writing) // Activities to explore (locked or not writing)
    .map(classItem => ({
      id: classItem._id, // Assuming _id is your unique identifier
      icon: classItem.icon || '📚', // Use classItem.icon or a default
      title: classItem.title,
      description: classItem.description,
      buttonText: classItem.locked ? 'Locked' : 'Enroll', // Or 'Go to Class' if it's unlocked and not writing
      buttonLink: classItem.link || '#', // Use classItem.link if available
      borderColor: classItem.locked ? '#9ca3af' : '#f97316', // Gray for locked, orange for unlocked explore
      isLocked: classItem.locked,
      currentClasses: classItem.completedClasses || 0, // Assuming a 'completedClasses' field
      requiredClasses: classItem.minCompletedClasses || 0,
      detailsText: classItem.details || `Learn more about ${classItem.title}. Required classes to unlock: ${classItem.minCompletedClasses || 0}.`,
      onButtonClick: classItem.locked ? null : () => window.location.href = classItem.link, // Only navigate if not locked
    }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 font-sans p-6">
        <div className="flex flex-col items-center p-8 bg-white rounded-3xl shadow-xl">
          <div className="animate-bounce bg-blue-500 p-4 rounded-full mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
          </div>
          <p className="text-xl text-gray-700 font-semibold">Loading profile data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <main className="flex-grow container mx-auto p-4 md:p-8 flex justify-center items-center h-screen">
        <p className="text-xl text-red-600">{error}</p>
      </main>
    );
  }

  return (
    <main className="flex-grow container mx-auto p-4 md:p-8 lg:max-w-screen-xl xl:max-w-screen-2xl font-inter">
      {/* Search and Filter Section */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border-b-4 border-blue-500 mb-6 transform-gpu transition-all duration-300 hover:scale-[1.005]">
        <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-4 flex items-center">
          <svg className="w-8 h-8 md:w-10 md:h-10 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          Find Your Next Activity
        </h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            id="activity-search"
            placeholder="Search by class name or description..."
            className="flex-grow p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            id="activity-filter"
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Classes</option>
            <option value="unlocked">Unlocked Classes</option>
            <option value="locked">Locked Classes</option>
          </select>
        </div>
      </div>

      {/* My Enrolled Activities Section */}
      {enrolledActivities.length > 0 && (
        <ActivitiesSection
          title="My Enrolled Activities"
          iconSvgPath="M5 13l4 4L19 7"
          iconColorClass="text-blue-500"
          borderColorClass="border-blue-500"
          activities={enrolledActivities}
        />
      )}

      {/* Explore More Activities Section */}
      {exploreActivities.length > 0 && (
        <ActivitiesSection
          title="Explore More Activities"
          iconSvgPath="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3v-7m6 7l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 12m-6-4l.01 0M15 12l.01 0"
          iconColorClass="text-orange-500"
          borderColorClass="border-orange-500"
          activities={exploreActivities}
        />
      )}

      {enrolledActivities.length === 0 && exploreActivities.length === 0 && !loading && !error && (
        <main className="flex-grow container mx-auto p-4 md:p-8 flex justify-center items-center">
          <p className="text-xl text-gray-600">No activities found matching your criteria.</p>
        </main>
      )}
            <CertificateCriteria open={drawerOpen} onClose={handleClickClose} />
      
    </main>

    
  );
};

export default Activities;